import React, { useState, useEffect, useRef } from 'react';
import { Layout, Menu, Avatar, Dropdown, Badge, Popover, Button, List, Modal, Input, App } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import {
  DashboardOutlined, SearchOutlined, CalendarOutlined,
  FileTextOutlined, TeamOutlined, InfoCircleOutlined,
  UserOutlined, LogoutOutlined, AimOutlined, BuildOutlined,
  ThunderboltOutlined, BellOutlined, BarChartOutlined,
  BookOutlined, SettingOutlined, MessageOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import api from '../api/client';
import dayjs from 'dayjs';
import OnboardingGuide, { onboardingKey } from './OnboardingGuide';

const { Sider, Content, Header } = Layout;

export default function AppLayout({ children }) {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user, logout, updateUser } = useUser();
  const [collapsed, setCollapsed] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [onboardOpen, setOnboardOpen] = useState(false);
  const { message: msg } = App.useApp();

  // First-login onboarding guide — auto-opens once per user (students & tutors only).
  useEffect(() => {
    if (!user?.userId) return;
    if (user.role !== 'student' && user.role !== 'tutor') return;
    if (localStorage.getItem(onboardingKey(user.userId))) return;
    setOnboardOpen(true);
  }, [user?.userId, user?.role]);

  const closeOnboarding = () => {
    if (user?.userId) localStorage.setItem(onboardingKey(user.userId), '1');
    setOnboardOpen(false);
  };

  const [notifs, setNotifs]       = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('notif_dismissed') || '[]')); } catch { return new Set(); }
  });
  const [seen, setSeen] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('notif_seen') || '[]')); } catch { return new Set(); }
  });

  // Sync avatar from DB once per login session in case JWT was issued before user set a profile pic.
  useEffect(() => {
    if (!user?.userId || user?.avatar) return;
    api.get(`/api/users/${user.userId}`)
      .then(r => { if (r.data?.user_avatar_link) updateUser({ avatar: r.data.user_avatar_link }); })
      .catch(() => {});
  }, [user?.userId]);

  // Cache notifications for 30s to avoid refetching on every route change.
  const lastFetchedRef = useRef(0);
  useEffect(() => {
    if (Date.now() - lastFetchedRef.current < 30000) return;
    api.get('/api/dashboard/notifications').then(r => {
      setNotifs(r.data.items || []);
      lastFetchedRef.current = Date.now();
    }).catch(() => {});
  }, [location.pathname]);

  const dismiss = (key) => {
    const next = new Set([...dismissed, key]);
    setDismissed(next);
    localStorage.setItem('notif_dismissed', JSON.stringify([...next]));
  };

  const markAllSeen = () => {
    const next = new Set([...seen, ...visibleNotifs.map(n => n.key)]);
    setSeen(next);
    localStorage.setItem('notif_seen', JSON.stringify([...next]));
  };

  const visibleNotifs = notifs.filter(n => !dismissed.has(n.key));
  const totalUnread   = visibleNotifs.filter(n => !seen.has(n.key)).length;

  const renderNotifContent = (n) => {
    switch (n.type) {
      case 'new_subscription':
        return { title: <><strong>{n.student_name}</strong> subscribed to <strong>{n.subject_name} {n.level ? `(${n.level})` : ''}</strong></>, sub: dayjs(n.created_at).format('D MMM · h:mm A') };
      case 'gate_submitted':
        return { title: <><strong>{n.student_name}</strong> submitted <strong>{n.stage_name}</strong> for review</>, sub: `${n.template_title} · ${dayjs(n.submitted_at).fromNow()}` };
      case 'exam_soon':
        return { title: <><strong>{n.student_name}</strong>'s exam is on <strong>{dayjs(n.exam_date).format('D MMM')}</strong></>, sub: `${n.template_title} · ${dayjs(n.exam_date).diff(dayjs(), 'day')}d away` };
      case 'no_template':
        return { title: <>Subscribed to <strong>{n.subject_name}</strong> with <strong>{n.tutor_name}</strong></>, sub: 'Waiting for tutor to assign work' };
      case 'gate_approved':
        return { title: <>Stage cleared: <strong>{n.stage_name}</strong></>, sub: `${n.template_title} · ${dayjs(n.completed_at).fromNow()}` };
      case 'gate_rejected':
        return { title: <>Needs rework: <strong>{n.stage_name}</strong></>, sub: n.tutor_feedback || n.template_title };
      case 'new_assignment':
        return { title: <><strong>{n.tutor_name}</strong> assigned you <strong>{n.template_title}</strong></>, sub: dayjs(n.assigned_at).fromNow() };
      case 'expiring_soon':
        return { title: <>Subscription expiring: <strong>{n.subject_name}</strong></>, sub: `Expires ${dayjs(n.end_date).format('D MMM YYYY')}` };
      case 'planner_shared':
        return { title: <><strong>{n.owner_name}</strong> shared a {n.item_type}: <strong>{n.title}</strong></>, sub: `Due ${dayjs(n.due_date).format('D MMM YYYY')}` };
      default:
        return { title: 'Notification', sub: '' };
    }
  };

  const notifContent = (
    <div style={{ width: 320 }}>
      {visibleNotifs.length === 0 ? (
        <div style={{ padding: '20px 0', textAlign: 'center', color: '#bbb', fontSize: 13 }}>
          No new notifications
        </div>
      ) : (
        <List
          dataSource={visibleNotifs}
          renderItem={item => {
            const { title, sub } = renderNotifContent(item);
            const isUnseen = !seen.has(item.key);
            return (
              <List.Item
                style={{ padding: '10px 0', alignItems: 'flex-start', background: isUnseen ? '#fafafa' : 'transparent' }}
                actions={[
                  <Button
                    type="text" size="small"
                    style={{ color: '#ccc', fontSize: 11, padding: '0 4px', height: 'auto' }}
                    onClick={() => dismiss(item.key)}
                  >
                    Dismiss
                  </Button>
                ]}
              >
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  {isUnseen && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#0a0a0a', marginTop: 6, flexShrink: 0 }} />}
                  <div style={{ paddingLeft: isUnseen ? 0 : 14 }}>
                    <div style={{ fontSize: 13, color: '#0a0a0a', lineHeight: 1.5 }}>{title}</div>
                    <div style={{ fontSize: 11, color: '#aaa', marginTop: 3 }}>{sub}</div>
                  </div>
                </div>
              </List.Item>
            );
          }}
        />
      )}
    </div>
  );

  const navItems = [
    { key: '/about',        icon: <InfoCircleOutlined />, label: 'About' },
    { key: '/dashboard',    icon: <DashboardOutlined />,  label: 'Dashboard' },
    { key: '/search',       icon: <SearchOutlined />,     label: 'Find Tutors' },
    { key: '/schedule',     icon: <CalendarOutlined />,   label: 'Planner' },
    { key: '/material',     icon: <FileTextOutlined />,   label: 'Materials' },
    { key: '/subscription', icon: <TeamOutlined />,       label: 'Subscriptions' },
    { key: '/stage-gate',   icon: <AimOutlined />,        label: 'Roadmap' },
    { key: '/analytics',    icon: <BarChartOutlined />,   label: 'Analytics' },
    ...(user?.role === 'tutor' || user?.role === 'admin'
      ? [
          { key: '/stage-gate/builder', icon: <BuildOutlined />,      label: 'Roadmap Builder' },
          { key: '/ai-usage',           icon: <ThunderboltOutlined />, label: 'AI Usage' },
        ]
      : []),
    ...(user?.role === 'admin'
      ? [
          { type: 'divider' },
          { key: '/admin/subjects',  icon: <BookOutlined />,    label: 'Subjects (admin)' },
          { key: '/admin/templates', icon: <SettingOutlined />, label: 'MOE Templates (admin)' },
          { key: '/admin/users',     icon: <TeamOutlined />,    label: 'Users (admin)' },
          // { key: '/admin/feedback',  icon: <MessageOutlined />, label: 'Feedback (admin)' },
        ]
      : []),
  ];

  const userMenu = {
    items: [
      { key: 'profile', icon: <UserOutlined />,  label: 'Profile' },
      { key: 'guide',   icon: <QuestionCircleOutlined />, label: 'How it works' },
      { key: 'logout',  icon: <LogoutOutlined />, label: 'Sign out', danger: true },
    ],
    onClick: ({ key }) => {
      if (key === 'profile') navigate('/profile');
      if (key === 'guide')   setOnboardOpen(true);
      if (key === 'logout')  { logout(); navigate('/'); }
    },
  };

  return (
    <Layout style={{ height: '100vh', overflow: 'hidden' }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        width={180}
        style={{ background: '#fff', borderRight: '1px solid #e8e8e8', overflow: 'hidden' }}
      >
        <div style={{
          height: 56,
          padding: collapsed ? '0 12px' : '0 24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          borderBottom: '1px solid #e8e8e8',
          marginBottom: 8,
        }}>
          {!collapsed ? (
            <>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#0a0a0a', letterSpacing: '-0.5px' }}>
                SGLearner
              </div>
              <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                {navItems.find(n => n.key === location.pathname)?.label ?? 'Dashboard'}
              </div>
            </>
          ) : (
            <div style={{ fontSize: 13, fontWeight: 800, color: '#0a0a0a', letterSpacing: '-0.5px', textAlign: 'center', width: '100%' }}>
              SG
            </div>
          )}
        </div>

        {/* Scrolls independently so all items stay reachable above the collapse trigger */}
        <div style={{ height: 'calc(100vh - 112px)', overflowY: 'auto' }}>
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            items={navItems}
            onClick={({ key }) => navigate(key)}
            style={{ border: 'none', fontSize: 13 }}
          />
        </div>
      </Sider>

      <Layout style={{ overflow: 'hidden' }}>
        <Header style={{
          background: '#fff',
          borderBottom: '1px solid #e8e8e8',
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 12,
          height: 56,
          flexShrink: 0,
        }}>
          <Button
            icon={<QuestionCircleOutlined />}
            onClick={() => setOnboardOpen(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: '#f5f5f5', border: '1px solid #e8e8e8', borderRadius: 16,
              color: '#0a0a0a', fontSize: 13, fontWeight: 500, height: 34, padding: '0 14px',
            }}
          >
            How it works
          </Button>

          <Popover
            content={notifContent}
            title={<span style={{ fontWeight: 600, fontSize: 13 }}>Notifications</span>}
            trigger="click"
            placement="bottomRight"
            arrow={false}
            overlayInnerStyle={{ padding: '8px 16px' }}
            onOpenChange={open => { if (open) markAllSeen(); }}
          >
            <Badge count={totalUnread} size="small">
              <Button
                type="text"
                shape="circle"
                icon={<BellOutlined style={{ fontSize: 16, color: totalUnread > 0 ? '#0a0a0a' : '#bbb' }} />}
              />
            </Badge>
          </Popover>

          <Dropdown menu={userMenu} placement="bottomRight">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <Avatar size={32} src={user?.avatar || undefined} style={{ background: '#0a0a0a', fontSize: 13 }}>
                {!user?.avatar && user?.username?.[0]?.toUpperCase()}
              </Avatar>
              <span style={{ fontSize: 13, color: '#444' }}>{user?.username}</span>
            </div>
          </Dropdown>
        </Header>

        <Content style={{ padding: 32, background: '#fafafa', overflowY: 'auto', position: 'relative' }}>
          {children}

          {/* Floating feedback button — hidden until tbl_feedback is created */}
          <button
            onClick={() => setFeedbackOpen(true)}
            style={{ display: 'none',
              position: 'fixed', bottom: 28, right: 28, zIndex: 999,
              background: '#0a0a0a', color: '#fff', border: 'none',
              borderRadius: 20, padding: '8px 16px 8px 12px',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 13, fontWeight: 500, boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
            }}
          >
            <MessageOutlined style={{ fontSize: 14 }} /> Feedback
          </button>
        </Content>
      </Layout>

      <Modal
        title="Send feedback"
        open={feedbackOpen}
        onCancel={() => { setFeedbackOpen(false); setFeedbackText(''); }}
        onOk={async () => {
          if (!feedbackText.trim()) { msg.warning('Please type your feedback'); return; }
          setFeedbackSending(true);
          try {
            await api.post('/api/feedback', { message: feedbackText.trim() });
            msg.success('Thanks! Your feedback has been sent.');
            setFeedbackOpen(false);
            setFeedbackText('');
          } catch (e) { msg.error(e.response?.data?.error || 'Failed to send'); }
          finally { setFeedbackSending(false); }
        }}
        okText="Send"
        confirmLoading={feedbackSending}
        okButtonProps={{ style: { background: '#0a0a0a', border: 'none' } }}
      >
        <p style={{ color: '#888', fontSize: 13, marginTop: 0, marginBottom: 12 }}>
          Found a bug, have a suggestion, or something unclear? Let us know.
        </p>
        <Input.TextArea
          rows={4}
          placeholder="Type your feedback here…"
          value={feedbackText}
          onChange={e => setFeedbackText(e.target.value)}
          autoFocus
        />
      </Modal>

      <OnboardingGuide
        open={onboardOpen}
        role={user?.role}
        onClose={closeOnboarding}
        navigate={navigate}
      />
    </Layout>
  );
}
